import React, { useEffect, useState } from 'react';
import { useDebounce } from '../../../hooks/useDebounce.js';
import { useLazyGetCalendarContactsQuery } from '../../../api/calendar.js';
import BaseAutocomplete from '../../form/BaseAutocomplete.jsx';
import BaseInput from '../../form/BaseInput.jsx';
import BaseDropdown from '../../form/BaseDropdown.jsx';
import BaseButton from '../../form/BaseButton.jsx';
import IconAdd from '../../images/IconAdd.jsx';
import BaseOverlay from '../../layout/BaseOverlay.jsx';

const ProjectContactsAddEdit = ({ addVisible, setAddVisible, contacts, setContacts, initialLead, setInitialLead }) => {
    const [search, setSearch] = useState('');
    const [contactList, setContactList] = useState([]);
    const [contactEmails, setContactEmails] = useState([]);
    const [newContact, setNewContact] = useState({
        contact_id: null,
        first_name: '',
        last_name: '',
        email: '',
        role: '',
        is_lead: false
    });
    const [showContactForm, setShowContactForm] = useState(false);
    const [showSearch, setShowSearch] = useState(true);
    const [error, setError] = useState('');

    const [getCalendarContacts, { isLoading: isLoadingContacts }] = useLazyGetCalendarContactsQuery();
    const debouncedSearch = useDebounce(search, 500);

    const handleAddNewContact = () => {
        setNewContact({
            contact_id: null,
            first_name: '',
            last_name: '',
            email: '',
            role: '',
            is_lead: false
        });
        setError('');
        setShowSearch(false);
        setShowContactForm(true);
    };

    const selectContact = (id) => {
        const contact = contactList.find((item) => item.id === id);
        setNewContact({
            contact_id: contact.id,
            first_name: contact.first_name || '',
            last_name: contact.last_name || '',
            email: contact.email || '',
            role: '',
            is_lead: false
        });
        setContactEmails(contact.emails || []);
        setError('');
        setShowContactForm(true);
        setShowSearch(false);
        setSearch('');
    };

    const resetForm = () => {
        setNewContact({
            contact_id: null,
            first_name: '',
            last_name: '',
            email: '',
            role: '',
            is_lead: false
        });
        setContactEmails([]);
        setShowContactForm(false);
        setShowSearch(true);
        setError('');
        setSearch('');
    };

    const setProjectLead = (value) => {
        if (!value) {
            if (contacts.length !== 0 && initialLead !== '') {
                setContacts((prev) =>
                    prev.map((contact) =>
                        contact.contact_id === initialLead ? { ...contact, is_lead: true } : contact
                    )
                );
            }
        }
        setNewContact((prev) => ({ ...prev, is_lead: value }));
    };

    const hideDialog = () => {
        setAddVisible(false);
    };

    const saveContact = () => {
        if (!newContact.first_name && !newContact.last_name) {
            setError('Please fill in the first name or the last name.');
        } else if (newContact.is_lead && newContact.email === '') {
            setError('The email is required when adding the project lead.');
        } else {
            setError('');
            setContacts([...contacts, { ...newContact, tempId: Date.now() }]);
            if (newContact.is_lead) {
                setInitialLead(newContact.contact_id || '');
            }
            resetForm();
            hideDialog();
        }
    };

    useEffect(() => {
        (async () => {
            if (debouncedSearch) {
                const result = await getCalendarContacts({ search: debouncedSearch });
                setContactList(result.data?.response?.contacts || []);
            } else {
                setContactList([]);
            }
        })();
    }, [debouncedSearch, getCalendarContacts]);

    return (
        <BaseOverlay show={addVisible} className='overflow-auto h-[100dvh] z-[1002] rounded-none'>
            <div className='min-h-96 max-w-3xl w-full mx-auto px-4'>
                <div className='flex justify-between items-center py-4'>
                    <div className='flex items-center gap-4'>
                        <div className='bg-theme1-lightBtns rounded-full p-2'>
                            <IconAdd className=' fill-white rounded-full w-6 h-6' />
                        </div>

                        <div className='text-theme1-title2 text-xl font-bold'>Key people</div>
                    </div>
                    <div className='text-theme1-primary font-bold' onClick={hideDialog}>
                        Close
                    </div>
                </div>
                <div className='p-2 pt-0'>
                    {showSearch && (
                        <BaseAutocomplete
                            items={contactList.map((contact) => ({
                                label: `${contact.first_name || ''} ${contact.last_name || ''}`,
                                value: contact.id
                            }))}
                            onSelect={selectContact}
                            value={search}
                            name='search'
                            setValue={setSearch}
                            exclude={contacts.map((contact) => contact.contact_id).filter(Boolean)}
                            placeholder='Search for a contact...'
                            isLoading={isLoadingContacts}
                        />
                    )}
                    {showContactForm && (
                        <>
                            {newContact.contact_id && (
                                <div className='font-bold text-theme1-primary border border-theme1-border1 py-2 px-4 rounded-lg'>
                                    {newContact.first_name} {newContact.last_name}
                                </div>
                            )}
                            {!newContact.contact_id && (
                                <>
                                    <BaseInput
                                        type='text'
                                        placeholder='First name'
                                        name='first_name'
                                        value={newContact.first_name}
                                        setValue={(value) => setNewContact((prev) => ({ ...prev, first_name: value }))}
                                    />
                                    <BaseInput
                                        className='mt-4'
                                        type='text'
                                        placeholder='Last name'
                                        name='last_name'
                                        value={newContact.last_name}
                                        setValue={(value) => setNewContact((prev) => ({ ...prev, last_name: value }))}
                                    />
                                </>
                            )}
                            {contactEmails.length > 0 ? (
                                <BaseDropdown
                                    className='mt-4'
                                    value={newContact.email}
                                    name='email'
                                    placeholder='Select Email'
                                    setValue={(value) => setNewContact((prev) => ({ ...prev, email: value }))}
                                    options={contactEmails.map((email) => ({ label: email, value: email }))}
                                />
                            ) : (
                                <BaseInput
                                    className='mt-4'
                                    type='text'
                                    placeholder='Email'
                                    name='email'
                                    value={newContact.email}
                                    setValue={(value) => setNewContact((prev) => ({ ...prev, email: value }))}
                                />
                            )}
                            <BaseInput
                                className='mt-4'
                                type='text'
                                placeholder='Role'
                                name='role'
                                value={newContact.role}
                                setValue={(value) => setNewContact((prev) => ({ ...prev, role: value }))}
                            />
                            <div className={'mt-4 flex'}>
                                <div
                                    className={`inline-flex w-auto rounded-2xl border border-theme1-text3 px-4 py-2 mb-2 items-center ${newContact.is_lead ? 'bg-theme1-primary text-theme1-white' : ''}`}
                                    onClick={() => setProjectLead(!newContact.is_lead)}
                                >
                                    Project Lead
                                </div>
                            </div>
                            {error && <div className='text-red-600 mt-2'>{error}</div>}
                            <div className='mt-4 flex justify-end gap-4'>
                                <BaseButton className='btn-secondary' textValue='Cancel' onClick={resetForm} />
                                <BaseButton className='btn-primary' textValue='Save' onClick={saveContact} />
                            </div>
                        </>
                    )}
                    {!showContactForm && !newContact.contact_id && (
                        <BaseButton
                            className='btn-primary-outline mt-4'
                            textValue='Add New Contact'
                            onClick={handleAddNewContact}
                        />
                    )}
                </div>
            </div>
        </BaseOverlay>
    );
};

export default ProjectContactsAddEdit;
